import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

const CACHE_KEY = 'sub_service_groups'

const COLOR_OPTIONS = ['amber', 'blue', 'emerald', 'violet', 'rose', 'sky', 'orange', 'teal', 'gray']
export { COLOR_OPTIONS }

/**
 * useSubServiceGroups — fetches sub_service_groups from Supabase.
 * Falls back to [] on error.
 *
 * @returns {{ groups, loading, error, createGroup, updateGroup, deleteGroup, reload }}
 */
export function useSubServiceGroups() {
    const [groups,  setGroups]  = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)

        const cached = await loadReferenceCache(CACHE_KEY, null).catch(() => null)
        if (cached) {
            setGroups(cached.data)
            if (!silent) setLoading(false)
        }

        const { data, error: err } = await supabase
            .from('sub_service_groups')
            .select('*')
            .order('sort_order', { ascending: true })
            .order('name',       { ascending: true })

        if (id !== loadIdRef.current) return

        if (err) {
            console.error('[subServiceGroups] fetch error:', err.message)
            setError(err.message)
            if (!cached) setGroups([])
        } else {
            setGroups(data || [])
            await saveReferenceCache(CACHE_KEY, data || [], null).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    const createGroup = useCallback(async ({ name, color = 'gray' }) => {
        const trimmed = name.trim().toUpperCase()
        if (!trimmed) throw new Error('name is required')
        const { data, error: err } = await supabase
            .from('sub_service_groups')
            .insert({ name: trimmed, color })
            .select()
            .single()
        if (err) {
            console.error('[subServiceGroups] insert error:', err.message)
            throw new Error(err.message)
        }
        setGroups(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)))
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
        return data
    }, [])

    const updateGroup = useCallback(async (id, patch) => {
        const { data, error: err } = await supabase
            .from('sub_service_groups')
            .update(patch)
            .eq('id', id)
            .select()
            .single()
        if (err) {
            console.error('[subServiceGroups] update error:', err.message)
            throw new Error(err.message)
        }
        setGroups(prev => prev.map(g => g.id === id ? data : g))
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
        return data
    }, [])

    const deleteGroup = useCallback(async (id) => {
        setGroups(prev => prev.filter(g => g.id !== id))
        const { error: err } = await supabase
            .from('sub_service_groups')
            .delete()
            .eq('id', id)
        if (err) {
            console.error('[subServiceGroups] delete error:', err.message)
            throw new Error(err.message)
        }
        await clearReferenceCache(CACHE_KEY, null).catch(() => {})
    }, [])

    return { groups, loading, error, createGroup, updateGroup, deleteGroup, reload: load, silentReload }
}
